import {
  CREDENTIAL_KINDS,
  upsertCredentialSchema,
  type CredentialKind,
  type CredentialSummary,
  type UpsertCredentialInput,
} from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { fetchCredentials, removeCredential, upsertCredential } from '../api/workspace.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { ConfirmDialog } from '../components/confirm-dialog.js'
import { DataTable, EmptyState, type Column } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field, SelectField } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { formatDateTime } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

const KIND_LABELS: Record<CredentialKind, string> = {
  chatapp: 'ChatApp — шлюз WhatsApp',
  openai: 'OpenAI — движок агента',
  keitaro: 'Keitaro — трекер конверсий',
  robot: 'Робот — каталог товаров',
  orderhub: 'OrderHub — CRM заявок',
}

interface FieldSpec {
  name: string
  label: string
  type?: 'text' | 'password' | 'url'
  hint?: string
  optional?: boolean
}

/**
 * Состав формы по видам.
 *
 * Описан здесь отдельно от схем Zod, и это единственное разумное место:
 * схема знает, какие значения допустимы, но не знает, что apiKey надо
 * показывать точками, а postbackUrl требует пояснения про ключ в пути.
 * Проверка при отправке всё равно идёт схемой из shared, так что разойтись
 * эти два описания могут только по подписям, а не по правилам.
 */
const FORMS: Record<CredentialKind, FieldSpec[]> = {
  chatapp: [
    { name: 'email', label: 'Почта кабинета ChatApp' },
    { name: 'password', label: 'Пароль кабинета', type: 'password' },
    {
      name: 'appId',
      label: 'appId',
      hint: 'Из кабинета ChatApp. Вебхуки и токены изолированы по нему, поэтому у тестового и рабочего окружения он разный',
    },
  ],
  openai: [
    { name: 'apiKey', label: 'Ключ API', type: 'password' },
    { name: 'organization', label: 'Организация', optional: true },
    { name: 'project', label: 'Проект', optional: true },
  ],
  robot: [
    { name: 'baseUrl', label: 'Адрес сервиса', type: 'url', hint: 'Без завершающего слэша' },
    {
      name: 'apiKey',
      label: 'Ключ API',
      type: 'password',
      hint: 'По нему раз в двенадцать часов забирается каталог товаров. Товары, приехавшие оттуда, правятся там же — у нас они только для чтения',
    },
  ],
  keitaro: [
    { name: 'trackerUrl', label: 'Адрес трекера', type: 'url', hint: 'Без завершающего слэша' },
    {
      name: 'postbackUrl',
      label: 'Postback URL целиком',
      type: 'url',
      hint: 'Скопируйте дословно из Settings → Postback URL. Ключ входит в путь адреса, собирать его из частей нельзя',
    },
    {
      name: 'returnToken',
      label: 'Строка подтверждения',
      hint: 'Передаётся в &return= и по ней опознаётся успех. Обычно OK',
    },
    { name: 'adminApiKey', label: 'Ключ Admin API', type: 'password', optional: true },
  ],
  orderhub: [
    { name: 'baseUrl', label: 'Адрес сервиса', type: 'url', optional: true },
    { name: 'token', label: 'Токен', type: 'password', optional: true },
    {
      name: 'note',
      label: 'Заметка',
      optional: true,
      hint: 'Сервис ещё не изучен, поэтому состав свободный — уточним после получения доступа',
    },
  ],
}

/**
 * Что можно завести через форму.
 *
 * Робота здесь нет нарочно. Он пускает по Basic-авторизации — логин и
 * пароль служебного пользователя, — и эта учётка живёт только в
 * `.env.stack` (ROBOT_BASE_URL, ROBOT_LOGIN, ROBOT_PASSWORD), как требует
 * их инструкция. Форма с «ключом API» принимала значение, которое никто
 * не читал: справочник групп берёт учётку из окружения, а синхронизация
 * каталога ещё не написана. Уже сохранённая запись остаётся в таблице,
 * чтобы её можно было удалить.
 */
const FORM_KINDS = CREDENTIAL_KINDS.filter((kind) => kind !== 'robot')

export function CredentialsPage() {
  const workspace = useCurrentWorkspace()
  const queryClient = useQueryClient()
  const [toRemove, setToRemove] = useState<CredentialSummary | null>(null)

  const credentials = useQuery({
    queryKey: ['credentials', workspace.id],
    queryFn: () => fetchCredentials(workspace.id),
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['credentials', workspace.id] })

  const remove = useMutation({
    mutationFn: (id: string) => removeCredential(workspace.id, id),
    onSuccess: () => {
      setToRemove(null)
      void invalidate()
    },
  })

  const columns: Column<CredentialSummary>[] = [
    {
      key: 'kind',
      header: 'Интеграция',
      render: (item) => (
        <div>
          <p className="text-sm text-ink">{KIND_LABELS[item.kind]}</p>
          <p className="font-mono text-xs text-muted">{item.label}</p>
        </div>
      ),
    },
    {
      key: 'hints',
      header: 'Что сохранено',
      render: (item) => (
        <dl className="flex flex-col gap-0.5">
          {Object.entries(item.hints).map(([key, value]) => (
            <div key={key} className="flex gap-2 text-xs">
              <dt className="text-muted">{key}</dt>
              <dd className="font-mono text-ink-soft">{value}</dd>
            </div>
          ))}
        </dl>
      ),
    },
    {
      key: 'verified',
      header: 'Проверено',
      render: (item) =>
        item.verifiedAt ? (
          <Badge tone="ok">{formatDateTime(item.verifiedAt)}</Badge>
        ) : (
          <Badge tone="warn">связь не проверялась</Badge>
        ),
    },
    {
      key: 'lastUsed',
      header: 'Использовано',
      align: 'right',
      render: (item) => (
        <span className="text-sm text-muted">{formatDateTime(item.lastUsedAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (item) => (
        <Button variant="ghost" size="sm" onClick={() => setToRemove(item)}>
          Удалить
        </Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Ключи интеграций"
        description="Значения шифруются на сервере и наружу не отдаются никогда — метода «показать секрет» в API не существует. Здесь видны только маски, по которым можно узнать свою запись."
      />

      <div className="flex flex-col gap-6">
        <DataTable
          rows={credentials.data ?? []}
          columns={columns}
          rowKey={(item) => item.id}
          isLoading={credentials.isPending}
          isRefreshing={credentials.isFetching && !credentials.isPending}
          empty={
            <EmptyState
              title="Ключей пока нет"
              description="Добавьте ключ OpenAI, чтобы агент заработал, и доступ к ChatApp — чтобы подключить WhatsApp."
            />
          }
        />

        {credentials.error && (
          <ErrorPanel error={credentials.error} onRetry={() => void credentials.refetch()} />
        )}

        <CredentialForm workspaceId={workspace.id} onSaved={invalidate} />
      </div>

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title="Удалить ключ?"
        description={
          <>
            {toRemove && KIND_LABELS[toRemove.kind]} перестанет работать сразу. Восстановить
            значение невозможно — оно зашифровано и нигде больше не хранится, ключ придётся
            вводить заново.
          </>
        }
        confirmLabel="Удалить"
        danger
        loading={remove.isPending}
        onConfirm={() => toRemove && remove.mutate(toRemove.id)}
      />
    </>
  )
}

function CredentialForm({
  workspaceId,
  onSaved,
}: {
  workspaceId: string
  onSaved: () => Promise<unknown>
}) {
  const [kind, setKind] = useState<CredentialKind>('openai')
  const [label, setLabel] = useState('default')
  const [values, setValues] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (input: UpsertCredentialInput) => upsertCredential(workspaceId, input),
    onSuccess: async () => {
      setValues({})
      setFormError(null)
      await onSaved()
    },
  })

  const spec = FORMS[kind]

  const submit = (event: FormEvent) => {
    event.preventDefault()

    // Пустые необязательные поля не отправляем: иначе схема получит
    // пустую строку там, где ждёт адрес или ключ, и откажет
    const payload: Record<string, string> = {}

    for (const field of spec) {
      const value = values[field.name]?.trim()
      if (value) payload[field.name] = value
    }

    const parsed = upsertCredentialSchema.safeParse({ kind, label: label || 'default', payload })

    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      setFormError(
        issue ? `${issue.path.join('.') || 'форма'}: ${issue.message}` : 'Проверьте поля',
      )
      return
    }

    setFormError(null)
    save.mutate(parsed.data)
  }

  return (
    <Panel
      title="Добавить или заменить ключ"
      description="Повторное сохранение того же вида и метки заменяет значение и сбрасывает отметку о проверке связи"
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Интеграция"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as CredentialKind)
              setValues({})
              setFormError(null)
            }}
          >
            {FORM_KINDS.map((value) => (
              <option key={value} value={value}>
                {KIND_LABELS[value]}
              </option>
            ))}
          </SelectField>

          <Field
            label="Метка"
            hint="Нужна, если у одной интеграции несколько наборов ключей"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {spec.map((field) => (
            <Field
              key={field.name}
              label={field.optional ? `${field.label} — необязательно` : field.label}
              type={field.type === 'url' ? 'url' : (field.type ?? 'text')}
              autoComplete={field.type === 'password' ? 'new-password' : 'off'}
              {...(field.hint ? { hint: field.hint } : {})}
              value={values[field.name] ?? ''}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, [field.name]: event.target.value }))
              }
            />
          ))}
        </div>

        {formError && <p className="text-sm text-crimson">{formError}</p>}
        {save.error && <ErrorPanel error={save.error} />}

        <div>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить
          </Button>
        </div>
      </form>
    </Panel>
  )
}
